import React from 'react';
import {GAME_PANEL_MATERIALS, GamePanel} from '../../gameUi/GamePanel';
import {GAME_BUTTON_SIZES, GAME_BUTTON_VARIANTS, GameButton} from '../../gameUi/GameButton';
import {EngravedIcon} from '../../gameUi/EngravedIcon';
import {joinClassNames} from '../../gameUi/joinClassNames';
import {BottomSheet} from '../../gameUi/BottomSheet';
import {useDesktopLayout} from '../../../hooks/visuals/useWideScreenLayout';
import {getCityCaptureFadeProps} from '../../gameUi/cityCapturePanelsFade';
import './HeaderDropdown.css';

// От какой кнопки выпадает список: строка игроков (правая зона шапки, на
// телефоне — полоса под шапкой), кнопка рынка (левая зона шапки, на
// телефоне — ярлыки у левого края) или плита ресурсов («мое» в шапке).
// Положение — CSS по якорю, без замеров
export const HEADER_DROPDOWN_ANCHORS = Object.freeze({
  PLAYERS: 'players',
  MARKET: 'market',
  STATS: 'stats',
});

/**
 * Выпадающий список шапки матча: плита базальта под своей кнопкой открытия,
 * высотой по содержимому (не выше окна — дальше прокрутка внутри). Закрытый
 * остается смонтированным, только скрыт: на постоянный монтаж опираются
 * селекторы обучения и карточки, открытые с кнопки хода.
 * На телефоне под списком прозрачная подложка: нажатие мимо закрывает список
 * и не доходит до поля. На десктопе подложки нет — поле вне списка управляемо
 *
 * @param {boolean} isOpen
 * @param {function(): void} onClose - крестик и нажатие мимо (телефон)
 * @param {string} anchor - HEADER_DROPDOWN_ANCHORS
 * @param {boolean} [isFocused] - верхний в стопке списков
 * @param {React.ReactNode} title - заголовок
 * @param {React.ReactNode} [headerExtra] - между заголовком и крестиком
 * @param {boolean} [isSheetOnPhone] - на телефоне — лист снизу во всю ширину
 *   (BottomSheet, закрывается и свайпом вниз), а не выпадающий список: так
 *   рынку хватает места. Классы header-dropdown_* сохраняются — на них
 *   опираются стрелки обучения и тесты
 */
const HeaderDropdown = ({
  isOpen,
  onClose,
  anchor,
  isFocused = false,
  title,
  headerExtra = null,
  isSheetOnPhone = false,
  className,
  children,
}) => {
  const isDesktopLayout = useDesktopLayout();

  const header = (
    <div className={'header-dropdown_header'}>
      <h2 className={'header-dropdown_title'}>{title}</h2>

      {headerExtra}

      <GameButton
        variant={GAME_BUTTON_VARIANTS.SUNK}
        size={GAME_BUTTON_SIZES.ICON}
        onClick={onClose}
        className={'header-dropdown_close-button'}
        aria-label={'Закрыть'}
      >
        <EngravedIcon src={'/img/icons/exit.png'} size={20}/>
      </GameButton>
    </div>
  );
  const body = (
    <div className={'header-dropdown_body'}>
      {children}
    </div>
  );

  if (isSheetOnPhone && !isDesktopLayout) {
    return (
      <BottomSheet
        isOpen={isOpen}
        onClose={onClose}
        header={header}
        className={joinClassNames(
          'header-dropdown_sheet',
          `header-dropdown_${anchor}`,
          isOpen && 'header-dropdown_open',
          className,
        )}
        role={'dialog'}
        {...getCityCaptureFadeProps(isOpen)}
      >
        {body}
      </BottomSheet>
    );
  }

  return (
    <>
      {isOpen && !isDesktopLayout && (
        <div
          className={'header-dropdown_backdrop'}
          onClick={onClose}
          aria-hidden={true}
        />
      )}

      <GamePanel
        material={GAME_PANEL_MATERIALS.SHEET}
        shadow
        className={joinClassNames(
          'header-dropdown',
          `header-dropdown_${anchor}`,
          isOpen && 'header-dropdown_open',
          isFocused && 'header-dropdown_focused',
          className,
        )}
        role={'dialog'}
        aria-hidden={!isOpen}
        {...getCityCaptureFadeProps(isOpen)}
      >
        {header}
        {body}
      </GamePanel>
    </>
  );
};

export default HeaderDropdown;
